import { useTranslation } from 'react-i18next'
import type { FiltroEstadoCarga } from '@/types'
import { DateRangePicker } from '@/components/common/DateRangePicker'
import { MultiFiltro, type OpcionMulti } from '@/components/common/MultiFiltro'
import { etiquetaHuecoDe } from '@/utils/filtroHuecos'
import { BarraFiltrosActivos, filtrosDe, type FiltroActivo } from '@/components/common/BarraFiltrosActivos'
import { filterFieldCls, filterLabelCls, filterCardCls } from '@/components/common/FilterBar'

export interface PedidosVentasFiltrosState {
  /** Nº de pedido y referencia que le pone el cliente: dos maneras de nombrar
   *  el MISMO pedido, la nuestra y la suya. */
  pedido: string[]
  /** Cuenta y nombre del cliente. */
  cliente: string[]
  /** Artículos de las líneas del pedido. */
  articulo: string[]
  /**
   * El buscador de un solo campo que había antes, donde número, cliente y
   * artículos caían en el mismo saco. Ningún control lo escribe ya: sobrevive
   * para que los enlaces `?q=…` que están en circulación sigan significando lo
   * que significaban cuando alguien los copió, en vez de dejar de filtrar sin
   * avisar a nadie.
   */
  terminos: string[]
  empresa: string
  estado: string
  familia: string
  soloPendientes: boolean
  /** Estado de carga. NO esconde pedidos: filtra qué cargas se pintan dentro de
   *  cada uno. El dato vive en las líneas de carga, que sólo se leen al
   *  desplegar, así que no puede decidir qué filas de la lista salen. */
  estadoCarga: FiltroEstadoCarga
  /** Clasificación comercial del cliente. Multiselección, e incluye el
   *  hueco como una opción más (ver `utils/filtroHuecos`). */
  grupo: string[]
  /**
   * ¿Enseño los pedidos que YA tienen carga, o los que no?
   *
   * Control APARTE de `estadoCarga` a propósito, aunque los dos hablen de
   * cargas: éste esconde pedidos y aquél no. Un mismo desplegable en el
   * que un valor filtra filas y los otros dos filtran sublíneas no hay
   * forma de explicarlo en un rótulo.
   */
  conCarga: '' | 'si' | 'no'
}

export const EMPTY_FILTROS: PedidosVentasFiltrosState = {
  pedido: [], cliente: [], articulo: [],
  terminos: [], empresa: '', estado: '', familia: '', soloPendientes: false,
  estadoCarga: '', grupo: [], conCarga: '',
}

/** Fecha ISO a dd/mm/aaaa para el chip. Un guion cuando el extremo está abierto. */
function fechaCorta(iso: string): string {
  return iso ? iso.split('-').reverse().join('/') : '…'
}

export function contarActivos(f: PedidosVentasFiltrosState) {
  return (f.pedido.length > 0 ? 1 : 0) + (f.cliente.length > 0 ? 1 : 0) +
         (f.articulo.length > 0 ? 1 : 0) +
         (f.terminos.length > 0 ? 1 : 0) + (f.empresa ? 1 : 0) + (f.estado ? 1 : 0) +
         (f.familia ? 1 : 0) + (f.soloPendientes ? 1 : 0) +
         (f.estadoCarga ? 1 : 0) +
         (f.grupo.length > 0 ? 1 : 0) + (f.conCarga ? 1 : 0)
}

interface Props {
  dark: boolean
  value: PedidosVentasFiltrosState
  onChange: (patch: Partial<PedidosVentasFiltrosState>) => void
  onClear: () => void
  desde: string
  hasta: string
  onDesde: (v: string) => void
  onHasta: (v: string) => void
  empresas: string[]
  showEmpresa: boolean
  estados: string[]
  familias: string[]
  /** Catálogo de grupos presentes en las filas, con el hueco si lo hay. */
  grupos: OpcionMulti[]
  /** Feedback de resultados, ya formateado por la página. */
  resumen?: string
}

export function PedidosVentasFiltros({
  dark, value, onChange, onClear, desde, hasta, onDesde, onHasta,
  empresas, showEmpresa, estados, familias, grupos, resumen,
}: Props) {
  const { t } = useTranslation()

  // Ahora cada término lleva delante de qué campo sale. Cuando había un solo
  // buscador no hacía falta —venían todos del mismo sitio—, pero con tres es
  // justo lo que hay que poder leer de un vistazo: si la lista salió corta,
  // saber si fue por el pedido o por el artículo.
  const filtrosActivos: FiltroActivo[] = [
    // El rango va como un chip más, y no sólo dentro de su control. Ahora que los
    // filtros sobreviven al cambio de pantalla, volver con un rango puesto y sin
    // rastro de él en la franja dejaba la lista corta sin explicación —y sin
    // «Limpiar todos», que sólo se pinta cuando hay algún chip.
    ...(desde || hasta
      ? [{
          clave: 'fechas', campo: t('common.filtros.fechas'),
          valor: [fechaCorta(desde), fechaCorta(hasta)].join(' – '),
          quitar: () => { onDesde(''); onHasta('') },
        }]
      : []),
    ...filtrosDe(value.pedido, pedido => onChange({ pedido }),
      { campo: t('common.busqueda.pedido'), prefijo: 'ped:' }),
    ...filtrosDe(value.cliente, cliente => onChange({ cliente }),
      { campo: t('common.busqueda.cliente'), prefijo: 'cli:' }),
    ...filtrosDe(value.articulo, articulo => onChange({ articulo }),
      { campo: t('common.busqueda.articulo'), prefijo: 'art:' }),
    // Los del buscador antiguo no llevan campo: no salen de ninguno en concreto,
    // y ponerles uno sería mentir sobre dónde buscó el enlace que los trajo.
    ...filtrosDe(value.terminos, terminos => onChange({ terminos }),
      { campo: t('common.busqueda.legado'), prefijo: 'q:' }),
    ...(value.empresa ? [{
      clave: 'emp', campo: t('common.company'), valor: value.empresa.toUpperCase(),
      quitar: () => onChange({ empresa: '' }),
    }] : []),
    ...(value.estado ? [{
      clave: 'est', campo: t('common.status'),
      valor: t('salesOrders.status.' + value.estado, { defaultValue: value.estado }),
      quitar: () => onChange({ estado: '' }),
    }] : []),
    ...(value.familia ? [{
      clave: 'fam', campo: t('common.family'), valor: value.familia,
      quitar: () => onChange({ familia: '' }),
    }] : []),
    ...(value.soloPendientes ? [{
      clave: 'pend', valor: t('salesOrders.filter.pendingOnly'),
      quitar: () => onChange({ soloPendientes: false }),
    }] : []),
    ...(value.estadoCarga ? [{
      clave: 'carga', campo: t('salesOrders.filter.loadState'),
      valor: t(value.estadoCarga === 'expedida'
        ? 'salesOrders.picking.expedida' : 'salesOrders.picking.enAlmacen'),
      quitar: () => onChange({ estadoCarga: '' as FiltroEstadoCarga }),
    }] : []),
    ...filtrosDe(value.grupo, grupo => onChange({ grupo }), {
      campo: t('salesOrders.filter.customerGroup'),
      etiquetaDe: etiquetaHuecoDe(t('common.filtros.sinInformar')),
    }),
    ...(value.conCarga ? [{
      clave: 'tcarga', campo: t('salesOrders.filter.hasLoad'),
      valor: t(value.conCarga === 'si'
        ? 'salesOrders.filter.withLoad' : 'salesOrders.filter.withoutLoad'),
      quitar: () => onChange({ conCarga: '' }),
    }] : []),
  ]


  return (
    <div className={filterCardCls(dark)}>
      <div className="flex flex-col gap-3 p-3 sm:flex-row sm:flex-wrap sm:items-end">
        <DateRangePicker dark={dark} desde={desde} hasta={hasta} onDesde={onDesde} onHasta={onHasta} />

        {/* TRES buscadores, no uno.
            Antes había un solo campo que buscaba a la vez en el número de
            pedido, el cliente, su referencia y los artículos. Dentro de un campo
            los términos se combinan con OR, así que escribir un pedido y luego
            un artículo pedía «lo uno O lo otro» y devolvía de más: no había
            manera de preguntar por un artículo DENTRO de un pedido. Separados,
            cada uno hace OR por dentro y todos se cruzan con AND por fuera, que
            es lo que la gente esperaba que hiciera desde el principio. */}
        <div className="flex flex-col gap-1 min-w-[150px] flex-1">
          <label className={filterLabelCls(dark)}>{t('common.busqueda.pedido')}</label>
          <MultiFiltro
            dark={dark}
            permitirLibre
            seleccion={value.pedido}
            onSeleccion={pedido => onChange({ pedido })}
            placeholder={t('common.search')}
            title={t('common.busqueda.pistaPegar')}
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[150px] flex-1">
          <label className={filterLabelCls(dark)}>{t('common.busqueda.cliente')}</label>
          <MultiFiltro
            dark={dark}
            permitirLibre
            seleccion={value.cliente}
            onSeleccion={cliente => onChange({ cliente })}
            placeholder={t('common.search')}
            title={t('common.busqueda.pistaPegar')}
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[150px] flex-1">
          <label className={filterLabelCls(dark)}>{t('common.busqueda.articulo')}</label>
          <MultiFiltro
            dark={dark}
            permitirLibre
            seleccion={value.articulo}
            onSeleccion={articulo => onChange({ articulo })}
            placeholder={t('common.search')}
            title={t('common.busqueda.pistaPegar')}
          />
        </div>

        {/* Empresa */}
        {showEmpresa && (
          <div className="flex flex-col gap-1 min-w-[90px]">
            <label className={filterLabelCls(dark)}>{t('common.company')}</label>
            <select
              value={value.empresa}
              onChange={e => onChange({ empresa: e.target.value })}
              className={`${filterFieldCls(dark, !!value.empresa)} cursor-pointer`}
            >
              <option value="">{t('common.allFem')}</option>
              {empresas.map(e => <option key={e} value={e}>{e.toUpperCase()}</option>)}
            </select>
          </div>
        )}

        {/* Estado */}
        <div className="flex flex-col gap-1 min-w-[130px]">
          <label className={filterLabelCls(dark)}>{t('common.status')}</label>
          <select
            value={value.estado}
            onChange={e => onChange({ estado: e.target.value })}
            className={`${filterFieldCls(dark, !!value.estado)} cursor-pointer`}
          >
            <option value="">{t('common.allStatuses')}</option>
            {estados.map(s => (
              <option key={s} value={s}>{t('salesOrders.status.' + s, { defaultValue: s })}</option>
            ))}
          </select>
        </div>

        {/* Familia */}
        {familias.length > 0 && (
          <div className="flex flex-col gap-1 min-w-[120px]">
            <label className={filterLabelCls(dark)}>{t('common.family')}</label>
            <select
              value={value.familia}
              onChange={e => onChange({ familia: e.target.value })}
              className={`${filterFieldCls(dark, !!value.familia)} cursor-pointer`}
            >
              <option value="">{t('common.allFamilies')}</option>
              {familias.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        )}

        {/* Grupo de clasificación del cliente (NAC / EXP).
            Multiselección y con el hueco incluido: sin esa opción, las filas
            sin grupo desaparecerían al filtrar sin que nada lo dijera. */}
        {grupos.length > 0 && (
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className={filterLabelCls(dark)}>
              {t('salesOrders.filter.customerGroup')}
            </label>
            <MultiFiltro
              dark={dark}
              opciones={grupos}
              seleccion={value.grupo}
              onSeleccion={grupo => onChange({ grupo })}
              placeholder={t('common.allFem')}
              titulosMono={false}
            />
          </div>
        )}

        {/* Con o sin carga. ESTE SÍ esconde pedidos: el dato está en la
            cabecera, no en las líneas de carga. */}
        <div className="flex flex-col gap-1 min-w-[130px]">
          <label className={filterLabelCls(dark)}>
            {t('salesOrders.filter.hasLoad')}
          </label>
          <select
            value={value.conCarga}
            onChange={e => onChange({ conCarga: e.target.value as '' | 'si' | 'no' })}
            className={`${filterFieldCls(dark, !!value.conCarga)} cursor-pointer`}
            title={t('salesOrders.filter.hasLoadHint')}
          >
            <option value="">{t('common.allMasc')}</option>
            <option value="si">{t('salesOrders.filter.withLoad')}</option>
            <option value="no">{t('salesOrders.filter.withoutLoad')}</option>
          </select>
        </div>

        {/* Estado de carga.
            Ojo: no esconde pedidos. Filtra las cargas que se pintan dentro de
            cada pedido desplegado, porque el dato vive en las líneas de carga y
            ésas sólo se leen al desplegar. */}
        <div className="flex flex-col gap-1 min-w-[130px]">
          <label className={filterLabelCls(dark)}>{t('salesOrders.filter.loadState')}</label>
          <select
            value={value.estadoCarga}
            onChange={e => onChange({ estadoCarga: e.target.value as FiltroEstadoCarga })}
            className={`${filterFieldCls(dark, !!value.estadoCarga)} cursor-pointer`}
            title={t('salesOrders.filter.loadStateHint')}
          >
            <option value="">{t('common.allFem')}</option>
            <option value="almacen">{t('salesOrders.picking.enAlmacen')}</option>
            <option value="expedida">{t('salesOrders.picking.expedida')}</option>
          </select>
        </div>

        {/* Toggle solo pendientes */}
        <div className="flex flex-col gap-1">
          <label className={filterLabelCls(dark)}>{t('salesOrders.filter.pendingLabel')}</label>
          <button
            onClick={() => onChange({ soloPendientes: !value.soloPendientes })}
            className={`text-[12px] px-2.5 py-1.5 rounded-lg border flex items-center gap-2 transition-colors cursor-pointer ${
              value.soloPendientes
                ? dark
                  ? 'border-sky-500/60 bg-sky-500/10 text-sky-400 ring-1 ring-sky-500/15'
                  : 'border-sky-400 bg-sky-50/60 text-sky-700 ring-1 ring-sky-300/25'
                : dark
                  ? 'bg-slate-800 border-white/10 text-slate-100'
                  : 'bg-white border-slate-200 text-slate-700'
            }`}
          >
            <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
              value.soloPendientes
                ? 'bg-sky-500 border-sky-500'
                : dark ? 'border-white/20' : 'border-slate-300'
            }`}>
              {value.soloPendientes && (
                <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M2 5l2.5 2.5L8 3"/>
                </svg>
              )}
            </span>
            {t('salesOrders.filter.pendingOnly')}
          </button>
        </div>

      </div>

      {/* Franja común, separada de los controles: éstos no se mueven nunca. */}
      {filtrosActivos.length > 0 && (
        <div className={`px-3 pb-3 -mt-1 ${dark ? 'border-white/8' : 'border-slate-200'}`}>
          <BarraFiltrosActivos
            dark={dark}
            filtros={filtrosActivos}
            onLimpiar={onClear}
            resumen={resumen}
          />
        </div>
      )}
    </div>
  )
}
